import React, { useState } from "react";
import {
  Checkbox,
  MenuItem,
  Select,
  TextField,
  ListSubheader,
} from "@mui/material";

type Option = {
  label: string;
  value: string;
};

type MultiSelectProps = {
  label: string;
  options: Option[];
  value: string[] | string;
  onChange: (newValues: string[]) => void;
  required?: boolean;
  className?: string;
  error?: string;
  onBlur?: () => void;
  disabled?: boolean;
  multiSelect?: boolean;
  search?: boolean;
  placeholder?: string;
};

const MultiSelectDropdown: React.FC<MultiSelectProps> = ({
  label,
  options,
  value,
  onChange,
  required = false,
  className = "",
  error,
  onBlur,
  disabled = false,
  multiSelect = false,
  search = true,
  placeholder = "Select...",
}) => {
  const [searchText, setSearchText] = useState("");
  const hasError = Boolean(error);

  const selectedValue = multiSelect ? (value as string[]) : [value as string];

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchText.toLowerCase()),
  );

  const handleChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | (Event & { target: { value: string[] | string; name: string } }),
  ) => {
    const selected = event.target.value;
    onChange(multiSelect ? (selected as string[]) : [selected as string]);
  };

  return (
    <div className={`mb-4 w-full ${className}`}>
      <label className="block text-sm font-medium text-[var(--color-text)] mb-2">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      <div className="relative w-full">
        <Select
          multiple={multiSelect}
          disabled={disabled}
          onBlur={onBlur}
          value={multiSelect ? selectedValue : selectedValue[0] || ""}
          onChange={handleChange}
          displayEmpty
          renderValue={(selected) => {
            const selectedArray = Array.isArray(selected)
              ? selected
              : [selected];

            if (!selectedArray[0]) {
              return (
                <span 
                  className="text-white/50" 
                  style={{ 
                    color: disabled ? "rgba(255, 255, 255, 0.5)" : undefined,
                    WebkitTextFillColor: disabled ? "rgba(255, 255, 255, 0.5)" : undefined 
                  }}
                >
                  {placeholder}
                </span>
              );
            }

            const firstLabel =
              options.find((o) => o.value === selectedArray[0])?.label ||
              selectedArray[0];

            return (
              <div className="flex items-center gap-2 flex-wrap max-w-full overflow-hidden">
                <span className="bg-[#B8F890] text-black text-xs px-3 py-1 rounded-full whitespace-nowrap">
                  {firstLabel}
                </span>
                {multiSelect && selectedArray.length > 1 && (
                  <span className="text-xs text-white/70 whitespace-nowrap">
                    +{selectedArray.length - 1} more
                  </span>
                )}
              </div>
            );
          }}
          IconComponent={() => (
            <svg
              className="w-4 h-4 text-[var(--color-stat-accent)] absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          )}
          className={`
            !w-full h-11 px-4 py-2 mt-1 pr-10 !rounded-full
            bg-[#2C293A] text-white
            ${hasError ? "border border-red-500" : "border border-transparent"}
            transition-all duration-200 ease-in-out
          `}
          sx={{
            "& .MuiOutlinedInput-notchedOutline": { border: "none" },
            "&.Mui-focused": { border: "2px solid var(--color-stat-accent) !important" },
            "&:hover:not(.Mui-disabled)": { border: "2px solid var(--color-stat-accent)" },
            "& .MuiSelect-icon": { display: "none" },
            "& .MuiSelect-select": { paddingRight: "2rem !important" },
            "&.Mui-disabled": {
              WebkitTextFillColor: "rgba(255, 255, 255, 0.5) !important",
              color: "rgba(255, 255, 255, 0.5) !important",
              opacity: 0.7,
            },
            "& .Mui-disabled .text-white\\/50": {
              color: "rgba(255, 255, 255, 0.5) !important",
            },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                backgroundColor: "var(--color-surface) !important",
                color: "var(--color-text) !important",
                border: "1px solid var(--color-border)",
                borderRadius: "12px",
                maxHeight: "300px",
                overflowY: "auto",
              }
            },
          }}
        >
          {multiSelect && options.length > 1 && (
            <ListSubheader
              disableSticky
              sx={{
                backgroundColor: "var(--color-surface)",
                px: 2,
                py: 1,
              }}
            >
              {search && (
                <TextField
                  size="small"
                  placeholder="Search..."
                  variant="outlined"
                  autoFocus
                  fullWidth
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  InputProps={{
                    sx: {
                      backgroundColor: "var(--color-bg)",
                      color: "var(--color-text)",
                      fontSize: "0.85rem",
                      borderRadius: "8px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        border: "1px solid var(--color-border)",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "var(--color-stat-accent)",
                      },
                    },
                  }}
                  InputLabelProps={{
                    style: { color: "var(--color-text)" },
                  }}
                />
              )}
            </ListSubheader>
          )}

          {options.length === 0 && (
            <MenuItem disabled sx={{ color: "var(--color-muted)" }}>
              No options available
            </MenuItem>
          )}

          {filteredOptions.length === 0 && options.length > 0 ? (
            <MenuItem disabled sx={{ color: "var(--color-muted)" }}>
              No matching results
            </MenuItem>
          ) : (
            filteredOptions.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
                disabled={disabled}
                sx={{
                  color: "var(--color-text)",
                  "&:hover": {
                    backgroundColor: "var(--color-divider)",
                  },
                }}
              >
                {multiSelect && (
                  <Checkbox
                    checked={selectedValue.includes(option.value)}
                    sx={{
                      color: "var(--color-text)",
                      "&.Mui-checked": {
                        color: "var(--color-stat-accent)",
                      },
                      padding: "0 8px 0 0",
                    }}
                  />
                )}
                {option.label}
              </MenuItem>
            ))
          )}
        </Select>
      </div>

      {hasError && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
};

export default MultiSelectDropdown;
